<template>
  <div class="collection-container">
    <collection-v2
      v-if="isInitCollectionV2"
      :feeds-list="feedsList"
      :collection-info="collectionInfo"
      :show-collection="showCollection"
      :active-index="activeIndex"
      :active-player-playing="activePlayerPlaying"
      @on-click-collectionItem="onClickCollectionItem"
      @on-click-checkLastCollection="onClickCheckLastCollection"
    />
    <transition
      v-if="isInitCollectionV2"
      name="slide-fade"
    >
      <collection-v2-title
        v-show="showCollection"
        @on-follow-collection="onFollowCollection"
      />
    </transition>
    <collection-v1
      v-if="isInitCollectionV1"
      @on-click-collectionItem="onClickCollectionItem"
      @on-click-old-collection-wrapper="onClickOldCollectionWrapper"
      @on-follow-collection="onFollowCollection"
      @on-click-checkLastCollection="onClickCheckLastCollection"
    >
      <template
        v-if="playRecTitle"
        #title
      >
        <div class="text-title">
          {{ playRecTitle }}
        </div>
      </template>
    </collection-v1>

    <transition
      v-if="isInitCollectionV1"
      name="slide-fade"
    >
      <collection-v1-title
        v-show="!showCollection"
      />
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useStore, MutationTypes } from '@/store/index';
import useVideoEventHandler from '@/composable/use-video-event-handler';
import { useCollectionContainerReport } from '@/composable/use-compoent-report-methods';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import { formatVideoCollectionSchema } from '@/utils/schema';
import { ModelMetaFeed, ModelCollectionInfo } from '@/types/model';
import { FeedReportOpt } from '@/types/feed';
import { EVideoEvtNameTypes } from '@/constants/video';
import CollectionV2 from './collection-v2/collection.vue';
import CollectionV1 from './collection-v1/collection.vue';
import CollectionV2Title from './collection-v2/collection-title.vue';
import CollectionV1Title from './collection-v1/collection-title.vue';

const { info: urlInfo } = useProvideUrlInfo();
const { id: feedId = '', collectionid: collectionId = '' } = urlInfo.query;
const store = useStore();
const { state } = store;
// props
interface Props {
  showRecInPlayPage: boolean,
  activeFeedReportOpt: FeedReportOpt,
  activeFeed: ModelMetaFeed,
}
const props = withDefaults(defineProps<Props>(), {});
const {
  showRecInPlayPage,
  activeFeedReportOpt,
  activeFeed,
} = toRefs(props);

const feedsList = computed(() => state.feedsList);
const showCollection = computed(() => state.showCollection);
const isNewUI = computed(() => state.isNewUI);
const activeIndex = computed(() => state.activeIndex);
const collectionInfo = computed(() => state.collectionInfo);
const activePlayerPlaying = computed(() => state.activePlayerPlaying);
// 是否初始化colectionV2组件
const isInitCollectionV2 = computed(() => state.isCollection && state.feedsList.length && state.isNewUI);
// 是否初始化colectionV1组件
const isInitCollectionV1 = computed(() => state.isCollection && state.feedsList.length && !state.isNewUI);

const { emitVideoEvent } = useVideoEventHandler();
const collectionContainerReport = useCollectionContainerReport({
  showRecInPlayPage,
  activeFeedReportOpt,
  activeFeed,
  feedsList,
});
collectionContainerReport.mountedReport();
const { openSchemaCheckInstallInQQAppendParams } = useGetCommonMethods();

const onClickCollectionItem = (feed: ModelMetaFeed, index: number) => {
  let eventName = EVideoEvtNameTypes.readyPlay;
  // 重复点击相同的视频卡片，视频需要播放暂停切换
  if (activeIndex.value === index) {
    eventName = EVideoEvtNameTypes.changePlayOrPause;
  } else {
    // 当老的精选集切换视频，新视频需要启动播放
    if (!isNewUI.value) {
      eventName = EVideoEvtNameTypes.play;
    };
  }
  store.commit(MutationTypes.SET_ACTIVE_INDEX, {
    value: index,
  });
  emitVideoEvent({
    index,
    eventName,
  });
};
const onClickCheckLastCollection = () => {
  if (showRecInPlayPage.value) {
    openSchemaCheckInstallInQQAppendParams(true, state.isCollection, 'weishi://main?goto=recommend');
  } else {
    const lastFeed = feedsList.value[feedsList.value.length - 1];
    // 如果是精选集最后一个视频，则打开最后一个，否则打开下一个
    const index = lastFeed.index < (collectionInfo.value as ModelCollectionInfo).feedNum
      ? lastFeed.index + 1
      : lastFeed.index;
    const id = feedsList.value.find(item => item.index === index)?.id || feedId;
    openSchemaCheckInstallInQQAppendParams(
      true,
      state.isCollection,
      formatVideoCollectionSchema(collectionId, id, index),
    );
  }
  collectionContainerReport.onClickCheckLastCollection();
};
const onFollowCollection = () => {
  const feedItem = feedsList.value[activeIndex.value];
  const { index } = feedItem;
  const id = feedItem?.id || feedId;
  openSchemaCheckInstallInQQAppendParams(
    true,
    state.isCollection,
    formatVideoCollectionSchema(collectionId, id, index),
  );
};
const onClickOldCollectionWrapper = () => {
  store.commit(MutationTypes.SET_SHOW_COLLECTION, {
    value: false,
  });
};

const playRecTitle = '';
</script>

<style>
</style>
